import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Shield, Eye, Lock, Users, FileText } from "lucide-react";
import NavBar from "@/components/NavBar";
import Logo from "@/components/Logo";
import { Link } from "react-router-dom";
import { ScrollArea } from "@/components/ui/scroll-area";

const PrivacyPolicy: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50">
      <NavBar />
      <div className="container mx-auto px-4 pt-24 pb-16 max-w-5xl">
        {/* Back Button */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="text-slate-600 hover:text-slate-900 hover:bg-slate-100"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </div>

        {/* Header Section */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-primary-500 to-accent-500 text-white px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Shield className="w-4 h-4" />
            Privacy & Security
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-gray-900 via-primary-600 to-accent-600 bg-clip-text text-transparent">
            Privacy Policy
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
            We are committed to protecting your privacy and ensuring
            transparency in how we handle your data.
          </p>
        </div>

        {/* Privacy Highlights */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-6 border border-white/50 text-center">
            <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-6 h-6 text-primary-600" />
            </div>
            <h3 className="font-semibold text-slate-900 mb-2">
              Data Protection
            </h3>
            <p className="text-sm text-slate-600">
              Industry-standard encryption and security measures
            </p>
          </div>
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-6 border border-white/50 text-center">
            <div className="w-12 h-12 bg-accent-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Eye className="w-6 h-6 text-accent-600" />
            </div>
            <h3 className="font-semibold text-slate-900 mb-2">Transparency</h3>
            <p className="text-sm text-slate-600">
              Clear information about data collection and usage
            </p>
          </div>
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-6 border border-white/50 text-center">
            <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Users className="w-6 h-6 text-emerald-600" />
            </div>
            <h3 className="font-semibold text-slate-900 mb-2">User Control</h3>
            <p className="text-sm text-slate-600">
              Full control over your data and preferences
            </p>
          </div>
        </div>

        {/* Policy Content */}
        <div className="bg-white/80 backdrop-blur-md rounded-2xl border border-white/60 shadow-xl overflow-hidden">
          <div className="p-8 border-b border-slate-200">
            <div className="flex items-center gap-3 mb-4">
              <FileText className="w-6 h-6 text-primary-600" />
              <h2 className="text-2xl font-bold text-slate-900">
                Privacy Policy
              </h2>
            </div>
            <p className="text-slate-600">
              <strong>Last updated: July 2024</strong>
            </p>
          </div>

          <ScrollArea className="h-[600px] w-full">
            <div className="p-8 space-y-8">
              <div>
                <p className="text-slate-700 leading-relaxed mb-6">
                  This Privacy Policy explains how Idea Bloom ("we", "us", or
                  "our") collects, uses, and protects your information when you
                  use our platform.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <span className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center text-sm font-bold text-primary-600">
                    1
                  </span>
                  Information We Collect
                </h3>
                <ul className="space-y-3 text-slate-700 leading-relaxed">
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-primary-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>
                      <strong>Account Information:</strong> Name, email address,
                      and password when you register.
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-primary-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>
                      <strong>Content:</strong> Ideas, comments, votes, and
                      feedback you submit.
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-primary-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>
                      <strong>Usage Data:</strong> Information about how you use
                      the platform (e.g., pages visited, actions taken).
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-primary-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>
                      <strong>Cookies:</strong> We use cookies to enhance your
                      experience and analyze usage.
                    </span>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <span className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center text-sm font-bold text-primary-600">
                    2
                  </span>
                  How We Use Your Information
                </h3>
                <ul className="space-y-3 text-slate-700 leading-relaxed">
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-accent-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>To provide and improve our services.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-accent-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>To personalize your experience.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-accent-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>
                      To communicate with you about updates, features, or
                      support.
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-accent-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>To ensure platform security and prevent abuse.</span>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <span className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center text-sm font-bold text-primary-600">
                    3
                  </span>
                  Sharing & Disclosure
                </h3>
                <ul className="space-y-3 text-slate-700 leading-relaxed">
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-emerald-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>
                      We do <strong>not</strong> sell your personal information.
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-emerald-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>
                      We may share data with trusted third-party service
                      providers (e.g., hosting, analytics) who help us operate
                      the platform.
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-emerald-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>
                      We may disclose information if required by law or to
                      protect our rights and users.
                    </span>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <span className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center text-sm font-bold text-primary-600">
                    4
                  </span>
                  Cookies & Tracking
                </h3>
                <p className="text-slate-700 leading-relaxed">
                  We use cookies and similar technologies to remember your
                  preferences, keep you logged in, and analyze site usage. You
                  can control cookies through your browser settings.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <span className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center text-sm font-bold text-primary-600">
                    5
                  </span>
                  Your Rights & Choices
                </h3>
                <ul className="space-y-3 text-slate-700 leading-relaxed">
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-accent-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>
                      You can access, update, or delete your account information
                      at any time.
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-accent-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>
                      You may opt out of marketing emails via the unsubscribe
                      link.
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-accent-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>
                      Contact us to request data deletion or for privacy-related
                      questions.
                    </span>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <span className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center text-sm font-bold text-primary-600">
                    6
                  </span>
                  Data Security
                </h3>
                <p className="text-slate-700 leading-relaxed">
                  We use industry-standard security measures to protect your
                  data. However, no method of transmission over the internet is
                  100% secure.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <span className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center text-sm font-bold text-primary-600">
                    7
                  </span>
                  Changes to This Policy
                </h3>
                <p className="text-slate-700 leading-relaxed">
                  We may update this Privacy Policy from time to time. We will
                  notify you of significant changes by posting a notice on the
                  platform.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <span className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center text-sm font-bold text-primary-600">
                    8
                  </span>
                  Contact Us
                </h3>
                <p className="text-slate-700 leading-relaxed">
                  If you have any questions or concerns about this Privacy
                  Policy, please contact us at{" "}
                  <a
                    href="mailto:support@fundorfling.com"
                    className="text-primary-600 hover:text-primary-700 underline"
                  >
                    support@fundorfling.com
                  </a>
                  .
                </p>
              </div>
            </div>
          </ScrollArea>
        </div>

        {/* Contact Section */}
        <div className="mt-16 bg-gradient-to-r from-primary-500 to-accent-500 rounded-2xl p-8 text-white text-center">
          <h3 className="text-2xl font-bold mb-4">Questions About Privacy?</h3>
          <p className="text-lg opacity-90 mb-6 max-w-2xl mx-auto">
            We're committed to transparency and protecting your privacy. Contact
            our team if you have any questions.
          </p>
          <Button
            variant="secondary"
            size="lg"
            className="bg-white text-primary-600 hover:bg-slate-100"
          >
            Contact Privacy Team
          </Button>
        </div>
      </div>

      {/* Footer */}
      <footer>
        <div className="border-t border-slate-200 py-8 text-center text-slate-600">
          <p>
            &copy; {new Date().getFullYear()} FundORFling. All rights reserved.
            Built for professional product teams.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
